import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import logo from "../images/safety-hero-logo.png";
import { XIcon, Camera, QrCode } from "lucide-react";
import jsQR from "jsqr";
import { useGameStore } from "../store/gameStore";

// 실제 존재하는 실루엣 이미지들만 import
import fireMonsterSilhouette from "../images/monsters/fire-monster-silhouette.png";
import wolfMonsterSilhouette from "../images/monsters/wolf-monster-silhouette.png";
import pointedMonsterSilhouette from "../images/monsters/pointed-monster-silhouette.png";
import electricMonsterSilhouette from "../images/monsters/electric-monster-silhouette.png";
import stairMonsterSilhouette from "../images/monsters/stair-monster-silhouette.png";
import gasMonsterSilhouette from "../images/monsters/gas-monster-silhouette.png";
import slipperyMonsterSilhouette from "../images/monsters/slippery-monster-silhouette.png";
import elbeMonsterSilhouette from "../images/monsters/elbe-monster-silhouette.png";

// 컬러 이미지들도 import (실제 파일이 있다면)
import fireMonsterColor from "../images/monsters/fire-monster.png";
import wolfMonsterColor from "../images/monsters/wolf-monster.png";
import pointedMonsterColor from "../images/monsters/pointed-monster.png";
import electricMonsterColor from "../images/monsters/electric-monster.png";
import stairMonsterColor from "../images/monsters/stair-monster.png";
import gasMonsterColor from "../images/monsters/gas-monster.png";
import slipperyMonsterColor from "../images/monsters/slippery-monster.png";
import elbeMonsterColor from "../images/monsters/elbe-monster.png";

const MonsterEncyclopedia = () => {
  const navigate = useNavigate();
  const [selectedMonster, setSelectedMonster] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [scannedQR, setScannedQR] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [qrScanResult, setQrScanResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const {
    completedMonsters,
    isGameActive,
    isGameCompleted,
    updatePlayTime,
    getFormattedPlayTime,
    getCompletedMonstersCount,
    completeGame,
  } = useGameStore();

  // 실시간 시간 업데이트
  useEffect(() => {
    if (isGameActive) {
      const interval = setInterval(() => {
        updatePlayTime();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isGameActive, updatePlayTime]);

  // 몬스터 이미지 선택 함수
  const getMonsterImage = (monsterType) => {
    if (completedMonsters.includes(monsterType)) {
      // 완료된 몬스터는 컬러 이미지 (현재는 실루엣 이미지 사용)
      // 실제 컬러 이미지가 있다면 아래 주석을 해제하고 사용
      switch (monsterType) {
        case "fire":
          return fireMonsterColor;
        case "wolf":
          return wolfMonsterColor;
        case "gas":
          return gasMonsterColor;
        case "slippery":
          return slipperyMonsterColor;
        case "electric":
          return electricMonsterColor;
        case "stair":
          return stairMonsterColor;
        case "elbe":
          return elbeMonsterColor;
        case "pointed":
          return pointedMonsterColor;
        default:
          return fireMonsterColor;
      }
    } else {
      // 미완료된 몬스터는 실루엣 이미지
      switch (monsterType) {
        case "fire":
          return fireMonsterSilhouette;
        case "wolf":
          return wolfMonsterSilhouette;
        case "gas":
          return gasMonsterSilhouette;
        case "slippery":
          return slipperyMonsterSilhouette;
        case "electric":
          return electricMonsterSilhouette;
        case "stair":
          return stairMonsterSilhouette;
        case "elbe":
          return elbeMonsterSilhouette;
        case "pointed":
          return pointedMonsterSilhouette;
        default:
          return fireMonsterSilhouette;
      }
    }
  };

  // 몬스터 이름 표시 함수
  const getMonsterName = (monsterType) => {
    if (completedMonsters.includes(monsterType)) {
      const monsterNames = {
        fire: "화재 몬스터",
        wolf: "추행 몬스터",
        gas: "가스 몬스터",
        slippery: "미끌 몬스터",
        electric: "전기 몬스터",
        stair: "계단 몬스터",
        elbe: "엘베 몬스터",
        pointed: "뾰족 몬스터",
      };
      return monsterNames[monsterType] || "???";
    } else {
      return "???";
    }
  };

  // 몬스터 상세 정보 데이터
  const monsterDetails = {
    fire: {
      name: "화재 몬스터",
      description: [
        "어린이를 놀래키는 것을 세상에서 가장 좋아하지만 소화기를 보면 부리나케 도망간다.",
        "불을 보고도 놀라지 않고 소화기로 달려가는 아이를 가장 싫어한다.",
      ],
    },
    wolf: {
      name: "추행 몬스터",
      description: [
        "어둡고 조용한 곳에 혼자 지나가는 어린이를 공격하기 위해 하루종일 기다린다.",
        "하지만 큰 소리가 나면 놀라 허겁지겁 도망간다.",
      ],
    },
    gas: {
      name: "가스 몬스터",
      description: [
        "가스 밸브를 잠그지 않은 어린이를 쫓아다니며 괴롭히는 것을 좋아한다.",
        "신선한 공기를 무서워해서 창문을 열면 재빠르게 숨어버린다.",
      ],
    },
    slippery: {
      name: "미끌 몬스터",
      description: [
        "실내에서 뛰는 어린이에게 몰래 다가가 다리를 걸어 넘어뜨리는 것을 좋아한다.",
        "가끔은 몰래 바닥에 물을 뿌려 뛰어다니는 어린이들이 미끄러지는 모습을 보고 깔깔 웃는다.",
      ],
    },
    electric: {
      name: "전기 몬스터",
      description: [
        "콘센트 구멍 속에서 숨어 지낸다.",
        "자신의 집인 콘센트 구멍 속으로 손가락, 젓가락 등이 들어오면 화를 내며 무서운 전기 공격을 퍼붓는다.",
      ],
    },
    stair: {
      name: "계단 몬스터",
      description: [
        "계단에서 뛰거나 장난치는 어린이를 가장 싫어한다.",
        "특히나 친구를 밀거나 놀래키는 장난을 하면 꿈에 나타나 하루종일 끝없는 계단을 걷게 한다고 한다.",
      ],
    },
    elbe: {
      name: "엘베 몬스터",
      description: [
        "엘리베이터 거울 속에서 숨어 살고있는 몬스터.",
        "엘리베이터에서 장난치는 어린이를 발견하면 엘리베이터가 움직일 때도 출입문을 확 열어버린다고 한다.",
      ],
    },
    pointed: {
      name: "뾰족 몬스터",
      description: [
        "뾰족몬스터의 가시는 바람이 불면 우수수 떨어져 이곳저곳에 가시가 퍼진다.",
        "특히 나무를 맨손으로 만지다가 뾰족몬스터의 가시에 찔리는 아이들이 많다고 한다.",
      ],
    },
  };

  const handleMonsterClick = (monsterType) => {
    setSelectedMonster({
      ...monsterDetails[monsterType],
      type: monsterType, // 몬스터 타입 추가
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMonster(null);
  };

  useEffect(() => {
    if (getCompletedMonstersCount() === 8) {
      completeGame();
      setIsSuccess(true);
    }
  }, [completedMonsters, getCompletedMonstersCount, completeGame]);
  // const openCamera = useCallback(async () => {
  //   // 카메라 권한 미리 확인
  //   try {
  //     const permissionStatus = await navigator.permissions.query({
  //       name: "camera",
  //     });

  //     if (permissionStatus.state === "denied") {
  //       alert(
  //         "카메라 권한이 거부되었습니다.\n브라우저 설정에서 카메라 권한을 허용해주세요."
  //       );
  //       return;
  //     }
  //   } catch (error) {
  //     console.log("권한 확인 API를 사용할 수 없습니다:", error);
  //   }

  //   setIsCameraOpen(true);
  //   setScannedQR(null);
  //   setScanning(false);
  //   setQrScanResult(null);
  // }, []);

  const openCamera = useCallback(async () => {
    setIsCameraOpen(true);
    setScannedQR(null);
    setScanning(false);
    setQrScanResult(null);
  }, []);

  // 카메라 닫기
  const closeCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraOpen(false);
    setScannedQR(null);
    setScanning(false);
    setQrScanResult(null);
  }, []);

  // 몬스터 해금 처리
  const handleMonsterUnlock = useCallback(
    (qrData) => {
      console.log("QR 코드 데이터:", qrData);

      let monsterName = "알 수 없는 몬스터";
      let monsterType = null;
      let quizUrl = null;

      // URL 형태의 QR 코드인지 확인
      if (qrData.includes("http")) {
        if (qrData.includes("quiz")) {
          // 직접 퀴즈 URL인 경우
          try {
            const url = new URL(qrData);
            const params = new URLSearchParams(url.search);
            monsterType = params.get("monster");

            // 몬스터 이름 매칭
            const monsterNames = {
              fire: "화재 몬스터",
              wolf: "추행 몬스터",
              gas: "가스 몬스터",
              slippery: "미끌 몬스터",
              electric: "전기 몬스터",
              stair: "계단 몬스터",
              elbe: "엘베 몬스터",
              pointed: "뾰족 몬스터",
            };
            monsterName = monsterNames[monsterType] || "알 수 없는 몬스터";

            const pathAndQuery = url.pathname + url.search;
            quizUrl = pathAndQuery;
          } catch (error) {
            console.error("URL 파싱 오류:", error);
          }
        } else {
          closeCamera();

          // 현재 페이지에서 직접 이동
          setTimeout(() => {
            window.location.href = qrData;
          }, 100);
          return true;
        }
      }

      if (monsterType) {
        // 사파리 브라우저 감지
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );

        if (isSafari) {
          // 사파리의 경우 즉시 네비게이션하고 카메라는 나중에 닫기
          alert(
            `${monsterName}가 발견되었습니다! 🎯\n\n이제 안전 수칙 퀴즈를 풀어 몬스터를 물리치세요!`
          );

          if (quizUrl) {
            // 사파리에서는 window.location을 사용하여 더 안정적인 네비게이션
            setTimeout(() => {
              window.location.href = quizUrl;
            }, 100);
          }
          setTimeout(() => {
            closeCamera();
          }, 300);
        } else {
          alert(
            `${monsterName}가 발견되었습니다! 🎯\n\n이제 안전 수칙 퀴즈를 풀어 몬스터를 물리치세요!`
          );

          // 카메라 닫기
          closeCamera();

          if (quizUrl) {
            setTimeout(() => {
              window.location.href = quizUrl;
            }, 100);
          }
        }
        return true;
      } else if (qrData.includes("http")) {
        alert(
          `QR 코드를 성공적으로 스캔했습니다! 🎯\n\n이제 안전 수칙 퀴즈를 풀어 몬스터를 물리치세요!: ${qrData}`
        );
        closeCamera();

        // 현재 페이지에서 직접 이동 (새 탭 열지 않음)
        setTimeout(() => {
          window.location.href = qrData;
        }, 100);
        return true;
      } else {
        alert(`알 수 없는 QR 코드입니다. 다시 시도해주세요.`);
        return false;
      }
    },
    [closeCamera]
  );

  const scanQRCode = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !scanning) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // 비디오가 준비되지 않았거나 크기가 0인 경우 리턴
    if (
      video.videoWidth === 0 ||
      video.videoHeight === 0 ||
      video.readyState < 2
    ) {
      return;
    }

    try {
      // 비디오 프레임을 캔버스에 그리기
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // 이미지 데이터 가져오기
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

      // jsQR로 QR 코드 감지
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });

      if (code) {
        setScannedQR(code.data);
        setScanning(false);

        // QR 코드 데이터에 따른 몬스터 해금 처리
        const isValidQR = handleMonsterUnlock(code.data);

        // 스캔 결과 설정
        setQrScanResult(isValidQR ? "success" : "error");

        // 사파리 브라우저 감지
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );

        // 유효한 QR 코드인 경우에만 자동으로 카메라 닫기
        if (isValidQR) {
          if (isSafari) {
            return;
          } else {
            // 다른 브라우저는 3초 후 자동으로 카메라 닫기
            setTimeout(() => {
              closeCamera();
            }, 3000);
          }
        } else {
          // 잘못된 QR 코드인 경우 2초 후 다시 스캔 가능하도록 설정
          setTimeout(() => {
            setScannedQR(null);
            setQrScanResult(null);
            setScanning(true);
          }, 2000);
        }
      }
    } catch (error) {
      // 스캔 중 에러는 무시하고 계속 진행
      console.warn("QR 스캔 중 에러:", error);
    }
  }, [scanning, handleMonsterUnlock, closeCamera]);

  // 카메라가 열렸을 때 자동 스캔 시작
  useEffect(() => {
    if (isCameraOpen && videoRef.current && scanning) {
      const interval = setInterval(scanQRCode, 300);
      return () => clearInterval(interval);
    }
  }, [isCameraOpen, scanning, scanQRCode]);

  // 카메라 모달이 열린 후 스트림 연결을 위한 개선된 useEffect
  useEffect(() => {
    let mounted = true;

    if (isCameraOpen && videoRef.current && !scanning) {
      const connectStream = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: "environment",
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });

          if (mounted && videoRef.current) {
            videoRef.current.srcObject = stream;
            streamRef.current = stream;

            // 비디오가 실제로 재생되기 시작할 때만 스캔 시작
            videoRef.current.onloadedmetadata = () => {
              if (mounted) {
                setScanning(true);
              }
            };

            videoRef.current.onplay = () => {
              // 재생이 시작되면 추가 확인
              if (
                mounted &&
                videoRef.current.videoWidth > 0 &&
                videoRef.current.videoHeight > 0
              ) {
                setScanning(true);
              }
            };

            videoRef.current.onerror = (error) => {
              console.error("비디오 에러:", error);
              if (mounted) {
                // 비디오 에러는 사용자에게 표시 (실제 재생 실패)
                alert("카메라 연결에 문제가 발생했습니다.");
                setIsCameraOpen(false);
              }
            };
          }
        } catch (error) {
          console.error("스트림 연결 실패:", error);
          if (mounted) {
            // 스트림이 실제로 작동하는지 먼저 확인
            setTimeout(async () => {
              if (
                mounted &&
                videoRef.current &&
                videoRef.current.videoWidth > 0
              ) {
                // 비디오가 실제로 작동하면 스캔 시작 (에러 메시지 표시 안함)
                console.log(
                  "getUserMedia 실패했지만 비디오 스트림은 사용 가능"
                );
                setScanning(true);
                return;
              }

              // 실제로 비디오가 작동하지 않는 경우에만 에러 처리
              if (error.name === "NotAllowedError") {
                alert(
                  "카메라 권한이 거부되었습니다.\n브라우저 설정에서 카메라 권한을 허용해주세요."
                );
                setIsCameraOpen(false);
              } else if (error.name === "NotFoundError") {
                alert(
                  "카메라를 찾을 수 없습니다.\n카메라가 연결되어 있는지 확인해주세요."
                );
                setIsCameraOpen(false);
              } else if (error.name === "SecurityError") {
                alert(
                  "보안 정책으로 인해 카메라에 접근할 수 없습니다.\nHTTPS 환경에서 사용해주세요."
                );
                setIsCameraOpen(false);
              } else if (error.name === "OverconstrainedError") {
                // 제약 조건 실패 시 더 관대한 설정으로 재시도
                try {
                  const fallbackStream =
                    await navigator.mediaDevices.getUserMedia({
                      video: { facingMode: "environment" },
                    });
                  if (mounted && videoRef.current) {
                    videoRef.current.srcObject = fallbackStream;
                    streamRef.current = fallbackStream;
                    setScanning(true);
                    return; // 성공하면 에러 메시지 표시하지 않음
                  }
                } catch (fallbackError) {
                  alert("카메라 설정에 문제가 있습니다.");
                  setIsCameraOpen(false);
                }
              } else {
                // 기타 에러는 로그만 남기고 사용자에게 표시하지 않음
                console.warn(
                  "카메라 연결 중 에러 발생, 하지만 계속 진행:",
                  error
                );
                // 에러가 발생해도 비디오가 작동할 가능성이 있으므로 카메라는 열어둠
              }
            }, 1500); // 좀 더 긴 대기 시간으로 비디오 스트림 안정화 대기
          }
        }
      };

      // DOM 렌더링 완료 후 스트림 연결
      const timer = setTimeout(connectStream, 200);

      return () => {
        mounted = false;
        clearTimeout(timer);
      };
    }
  }, [isCameraOpen, scanning]);

  return (
    <div className="flex flex-col p-4">
      <header className="text-center pb-2">
        <div className="relative">
          <div className="flex justify-center px-4 py-4 bg-white rounded-lg shadow-lg border-4 border-green-600">
            <h1 className="text-3xl text-black font-bold">몬스터 도감</h1>
          </div>
        </div>
        <p className="text-xl text-black mt-4 font-bold max-w-md mx-auto">
          모든 몬스터를 처치하여 도감을 완성하자!
        </p>
        <p className="text-base font-semibold text-gray-600 mt-2">
          수집 완료: {getCompletedMonstersCount()} / 8
        </p>
      </header>

      {/* 몬스터 그리드 */}
      <section
        className={`flex-1 flex items-center justify-center ${
          isGameCompleted ? "mb-2" : "mb-10"
        }`}
      >
        <div className="grid grid-cols-3 gap-2 w-full max-w-md">
          {/* 1행 */}
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("fire")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("fire")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("fire")}
                alt="Fire Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("fire")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("fire")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("fire")}
              </p>
            </div>
          </div>

          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("wolf")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("wolf")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("wolf")}
                alt="Wolf Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("wolf")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("wolf")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("wolf")}
              </p>
            </div>
          </div>

          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("gas")}
          >
            <div
              className={`w-24 h-24 rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("gas")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("gas")}
                alt="Gas Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("gas")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("gas")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("gas")}
              </p>
            </div>
          </div>

          {/* 2행 */}
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("slippery")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("slippery")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("slippery")}
                alt="Slippery Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("slippery")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("slippery")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("slippery")}
              </p>
            </div>
          </div>

          {/* 가운데 빈 공간 */}
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 bg-transparent rounded-xl border-2 border-transparent flex items-center justify-center mb-2"></div>
          </div>

          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("electric")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("electric")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("electric")}
                alt="Electric Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("electric")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("electric")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("electric")}
              </p>
            </div>
          </div>

          {/* 3행 */}
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("stair")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("stair")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("stair")}
                alt="Stair Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("stair")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("stair")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("stair")}
              </p>
            </div>
          </div>

          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("elbe")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("elbe")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("elbe")}
                alt="Elbe Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("elbe")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("elbe")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("elbe")}
              </p>
            </div>
          </div>

          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleMonsterClick("pointed")}
          >
            <div
              className={`w-[90px] h-[90px] rounded-xl border-2 flex items-center justify-center shadow-lg mb-2 hover:shadow-xl transition-all duration-300 ${
                completedMonsters.includes("pointed")
                  ? "bg-green-100 border-green-500"
                  : "bg-white/80 border-gray-300"
              }`}
            >
              <img
                src={getMonsterImage("pointed")}
                alt="Pointed Monster"
                className="w-full h-full object-contain p-2"
              />
            </div>
            <div
              className={`rounded-lg px-3 py-1 shadow-md ${
                completedMonsters.includes("pointed")
                  ? "bg-green-100 border border-green-300"
                  : "bg-white/90"
              }`}
            >
              <p
                className={`text-xs md:text-sm font-medium text-center ${
                  completedMonsters.includes("pointed")
                    ? "text-green-800"
                    : "text-gray-800"
                }`}
              >
                {getMonsterName("pointed")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="flex items-center justify-between px-4">
        <div className="flex flex-col items-center bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-bold rounded-lg px-4 py-2 mb-2 shadow-lg">
          <p className="text-xl font-bold text-gray-800 text-center">
            현재 기록
          </p>
          <div className="text-xl font-bold text-gray-800">
            {getFormattedPlayTime()}
          </div>
        </div>

        <button
          className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-full flex items-center justify-center shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95"
          onClick={openCamera}
        >
          <Camera className="w-8 h-8 text-white" />
        </button>

        <div className="flex flex-col items-center">
          <button className="w-24 h-24 mb-2">
            <img
              src={logo}
              alt="Safety Hero Logo"
              className="w-full h-full object-contain"
            />
          </button>
        </div>
      </section>

      {/* 게임 완료 시 미션 완료 버튼 표시 */}
      {isGameCompleted && (
        <section className="px-4 pb-6">
          <button
            onClick={() => navigate("/ranking-board")}
            className="w-full px-8 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95 flex items-center justify-center space-x-2"
          >
            <span className="text-2xl">🏆</span>
            <span>미션 완료하기</span>
          </button>
        </section>
      )}

      {/* 카메라 모달 */}
      {isCameraOpen && (
        <div className="fixed inset-0 bg-black flex flex-col z-50">
          {/* 카메라 헤더 */}
          <div className="flex justify-between items-center p-4 bg-black/80 text-white">
            <h2 className="text-xl font-bold flex items-center">
              <QrCode className="w-6 h-6 mr-2" />
              몬스터 QR 스캔
            </h2>
            <button
              onClick={closeCamera}
              className="text-white hover:text-gray-300 text-2xl font-bold p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          {/* 카메라 뷰 */}
          <div className="flex-1 relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ backgroundColor: "#000" }}
            />

            {/* 스캔 가이드 */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 border-4 border-white rounded-lg relative">
                <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-blue-400"></div>
                <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-blue-400"></div>
                <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-blue-400"></div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-blue-400"></div>
              </div>
            </div>

            {/* 스캔 상태 */}
            {scannedQR && qrScanResult === "success" && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg">
                <p className="text-center font-bold">QR 코드 스캔 성공! 🎉</p>
                <p className="text-sm mt-1">몬스터를 발견했다!</p>
                <p className="text-xs mt-1 opacity-75">{scannedQR}</p>
              </div>
            )}

            {scannedQR && qrScanResult === "error" && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg">
                <p className="text-center font-bold">잘못된 QR 코드 ❌</p>
                <p className="text-sm mt-1">다시 시도해주세요</p>
                <p className="text-xs mt-1 opacity-75">{scannedQR}</p>
              </div>
            )}

            {/* 스캔 안내 */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg">
              <p className="text-center text-sm">
                몬스터 QR 코드를 사각형 안에 맞춰주세요
              </p>
            </div>
          </div>

          {/* 숨겨진 캔버스 */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}

      {/* 몬스터 상세 정보 모달 */}
      {isModalOpen && selectedMonster && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-end mb-4">
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            {/* 몬스터 이미지와 이름 */}
            <div className="flex flex-col items-center mb-8">
              <div
                className={`w-36 h-36 rounded-full border-4 shadow-lg mb-4 flex items-center justify-center ${
                  completedMonsters.includes(selectedMonster.type)
                    ? "bg-green-100 border-green-500"
                    : "bg-white border-gray-300"
                }`}
              >
                <img
                  src={getMonsterImage(selectedMonster.type)}
                  alt={selectedMonster.name}
                  className="w-full h-full object-contain p-4"
                />
                {/* {completedMonsters.includes(selectedMonster.type) && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                )} */}
              </div>
              <h2
                className={`text-2xl font-bold text-center border-b-2 pb-2 ${
                  completedMonsters.includes(selectedMonster.type)
                    ? "text-green-800 border-green-500"
                    : "text-gray-800 border-blue-500"
                }`}
              >
                {completedMonsters.includes(selectedMonster.type)
                  ? selectedMonster.name
                  : "???"}
              </h2>
              {completedMonsters.includes(selectedMonster.type) && (
                <p className="text-sm text-green-600 mt-2 font-medium">
                  ✅ 수집 완료!
                </p>
              )}
              {!completedMonsters.includes(selectedMonster.type) && (
                <p className="text-sm text-gray-500 mt-2 font-medium">
                  몬스터를 처치 시 이미지가 해금됩니다
                </p>
              )}
            </div>

            {/* 몬스터 설명 */}
            <div className="space-y-4 mb-6">
              {selectedMonster.description.map((line, index) => (
                <p
                  key={index}
                  className="text-base text-gray-700 leading-relaxed text-left"
                >
                  {line}
                </p>
              ))}
            </div>

            {/* 닫기 버튼 */}
            <div className="flex justify-center">
              <button
                onClick={closeModal}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 성공 모달 */}
      {isSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-center mb-4">
              몬스터를 전부 처치했다!
            </h2>
            <p className="text-gray-600 mb-6">
              이제 당신은 진정한{" "}
              <span className="text-blue-500 font-bold">안전히어로</span>가
              되었습니다!
            </p>
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => navigate("/ranking-board")}
                className="w-full px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95"
              >
                🏆 미션 완료하기
              </button>
              <button
                onClick={() => setIsSuccess(false)}
                className="w-full px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95"
              >
                📖 도감 더 살펴보기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonsterEncyclopedia;
